/**
 * Flash sign-in, as quote-edit uses it: the accounts site leaves the signed-in user's Cognito id token in the
 * `flash.id_token` cookie, and the console sends it to the service as a Bearer token. The console never trusts the
 * token itself: the service's gateway verifies it, and the service decides who is staff (ADR-0006).
 */
export const FLASH_TOKEN_COOKIE = 'flash.id_token';

/** A cookie's value, or null when it isn't set. */
export function readCookie(name: string, cookies: string = document.cookie): string | null {
    for (const part of cookies.split(';')) {
        const [key, ...value] = part.trim().split('=');
        if (key === name) {
            try {
                return decodeURIComponent(value.join('=')) || null;
            } catch {
                return null;
            }
        }
    }
    return null;
}

/** The signed-in user's token, or null (always null on a laptop, where there is no Flash). */
export const flashToken = (): string | null => readCookie(FLASH_TOKEN_COOKIE);

/**
 * Whether the token has expired, or will within `marginS` seconds, going by its own `exp` claim. It only decides
 * when to renew the sign-in before a request would be refused; an unreadable token counts as expired.
 */
export function tokenExpired(token: string, nowMs: number = Date.now(), marginS: number = 60): boolean {
    const payload = token.split('.')[1];
    if (!payload) return true;
    try {
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        const { exp } = JSON.parse(json) as { exp?: unknown };
        return typeof exp !== 'number' || exp * 1000 <= nowMs + marginS * 1000;
    } catch {
        return true;
    }
}

/** The accounts site's sign-in page, which renews the token and comes back to `next` (quote-edit's `refresh=1`). */
export function loginUrl(accountsDomain: string, next: string): string {
    return `${accountsDomain.replace(/\/+$/, '')}/login/?refresh=1&next=${encodeURIComponent(next)}`;
}

/** Leaves the console for another site (the accounts sign-in). */
export function redirectTo(url: string): void {
    window.location.assign(url);
}
